/*
 * ************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *   Copyright 2020 Adobe Systems Incorporated
 *   All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by all applicable intellectual property
 * laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 * ************************************************************************
 */

import * as R from 'ramda';
import React from 'react';
import classnames from 'classnames';
import { defineMessages, injectIntl } from '@nebula/izzy/intl';

import Button from '@react/react-spectrum/Button';
import SplitButton from '@react/react-spectrum/SplitButton';
import RemoveCircle from '@react/react-spectrum/Icon/RemoveCircle';
import AddCircle from '@react/react-spectrum/Icon/AddCircle';
import Gears from '@react/react-spectrum/Icon/Gears';
import Save from '@react/react-spectrum/Icon/SaveFloppy';
import Delete from '@react/react-spectrum/Icon/DeleteOutline';
import { MenuItem } from '@react/react-spectrum/Menu';
import Heading from '@react/react-spectrum/Heading';
import SideRailList from '../common/plugin.view.nav.list';

import styles from './styles/side.rail.styl';

const getPluginNamespaces = R.pluck('namespace');

const MSG = defineMessages({
  applyOrg: {
    id: 'gemini.session.view.applyOrg',
    defaultMessage: 'Save as Org Default'
  },
  applyUser: {
    id: 'gemini.session.view.applyUser',
    defaultMessage: 'Save as User Default'
  },
  configure: {
    id: 'gemini.session.view.configure',
    defaultMessage: 'Configure'
  },
  done: {
    id: 'gemini.session.view.finishConfig',
    defaultMessage: 'Save'
  },
  endSession: {
    id: 'gemini.session.view.endSession',
    defaultMessage: 'End Session'
  },
  openSettings: {
    id: 'gemini.session.view.openSettings',
    defaultMessage: 'Manage Defaults'
  },
  reset: {
    id: 'gemini.session.view.reset',
    defaultMessage: 'Reset'
  },
  sessionDetails: {
    id: 'gemini.session.view.sessionDetails',
    defaultMessage: 'Session Details'
  },
  sessions: {
    id: 'gemini.session.view.sessions',
    defaultMessage: 'Sessions'
  },
  availableHead: {
    id: 'gemini.session.view.availableHead',
    defaultMessage: 'Available'
  }
});

const ConfigList = ({
  isConfigure,
  itemPropsFunc,
  plugins,
  heading,
  onSelect
}) => (isConfigure && plugins.length > 0 && (
  <React.Fragment>
    <Heading variant="subtitle2">{heading}</Heading>
    <SideRailList
      plugins={plugins}
      onSelectView={onSelect}
      itemPropsFunc={itemPropsFunc}
    />
  </React.Fragment>
));

const SessionViewSideRail = ({
  availableViewPlugins = [],
  isConfigure,
  izzy,
  onApplyConfigToOrg,
  onApplyConfigToUser,
  onConfigurePlugins,
  onUpdatePluginConfig,
  onOpenSettings,
  onSelectView,
  onToggleRail,
  railOpen,
  selectedView,
  useConfigure,
  viewPlugins
}) => {
  const shouldToggleRail = () => !isConfigure && onToggleRail();
  const allPlugins = R.flatten(viewPlugins);

  return (
    <div
      className={classnames(
        styles.railOuter,
        railOpen ? styles.bigRail : styles.smallRail,
        isConfigure && styles.isConfigure
      )}
    >
      <div className={styles.rail}>
        <div
          className={styles.railNav}
        >
          {!isConfigure && (
            <SideRailList
              itemPropsFunc={() => ({ showInfoIcon: false })}
              plugins={viewPlugins}
              onSelectView={onSelectView}
              selectedView={selectedView}
            />
          )}
          <ConfigList
            isConfigure={isConfigure}
            plugins={viewPlugins}
            onSelect={(namespace) => {
              if (viewPlugins.length > 1) {
                onUpdatePluginConfig(R.without([namespace], getPluginNamespaces(allPlugins)));
              }
            }}
            itemPropsFunc={namespace => ({
              disabled: selectedView === namespace,
              toggleIcon:
                viewPlugins.length > 1 && selectedView !== namespace ? (
                  <RemoveCircle
                    className={styles.redIcon}
                    size="S"
                  />
                ) : null
            })}
          />
          <ConfigList
            heading={izzy(MSG.availableHead)}
            isConfigure={isConfigure}
            plugins={availableViewPlugins}
            onSelect={namespace =>
              onUpdatePluginConfig(R.append(namespace, getPluginNamespaces(allPlugins)))}
            itemPropsFunc={() => ({
              toggleIcon: (
                <AddCircle
                  className={styles.railItemButton}
                  size="S"
                />
              )
            })}
          />
        </div>
      </div>
      <div
        onKeyDown={shouldToggleRail}
        onClick={shouldToggleRail}
        className={classnames({ [styles.railToggle]: !isConfigure })}
      />
      {useConfigure && (
        <span className={styles.buttonBar}>
          <div>
            {railOpen && !isConfigure && (
              <Button
                id="startConfigure"
                className={styles.configureButton}
                label={izzy(MSG.configure)}
                onClick={onConfigurePlugins}
              />
            )}
            {isConfigure && (
              <SplitButton
                id="exitConfigure"
                label={izzy(MSG.done)}
                onClick={onConfigurePlugins}
                onSelect={v => (
                  v === 'applyOrg' ? onApplyConfigToOrg()
                    : v === 'applyUser' ? onApplyConfigToUser()
                      : v === 'reset' ? onUpdatePluginConfig([])
                        : onOpenSettings()
                )}
              >
                <MenuItem
                  icon={<Delete />}
                  value="reset"
                >
                  {izzy(MSG.reset)}
                </MenuItem>
                <MenuItem
                  icon={<Save />}
                  value="applyOrg"
                >
                  {izzy(MSG.applyOrg)}
                </MenuItem>
                <MenuItem
                  icon={<Save />}
                  value="applyUser"
                >
                  {izzy(MSG.applyUser)}
                </MenuItem>
                <MenuItem
                  icon={<Gears />}
                  value="settings"
                >
                  {izzy(MSG.openSettings)}
                </MenuItem>
              </SplitButton>
            )}
          </div>
        </span>
      )}
    </div>
  );
};


SessionViewSideRail.displayName = 'SessionViewSideRail';

export default injectIntl(SessionViewSideRail);
